import {ActionList, ActionMenu} from '@primer/react'
import {graphql, useFragment} from 'react-relay'

import {LABELS} from '../../constants/labels'
import {getLinkedSecurityAlerts} from '../../utils/linked-security-alerts'
import type {LinkedSecurityAlertsFragment$key} from './__generated__/LinkedSecurityAlertsFragment.graphql'
import {
  LinkedAlertName,
  LinkedSecurityAlertIcon,
  LinkedSecurityAlertLinkItem,
  LinkedSecurityAlertListItem,
} from './LinkedSecurityAlert'
import styles from './LinkedSecurityAlerts.module.css'
import {LinkedSecurityAlertsPill} from './LinkedSecurityAlertsPill'

export type LinkedSecurityAlertsProps = {
  issueKey: LinkedSecurityAlertsFragment$key
  isSmall?: boolean
  isSticky?: boolean
}

export const LinkedSecurityAlerts = ({issueKey, isSmall = false, isSticky = false}: LinkedSecurityAlertsProps) => {
  const data = useFragment(
    // eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
    graphql`
      fragment LinkedSecurityAlertsFragment on Issue {
        repository {
          nameWithOwner
        }
        securityAlerts(first: 50) {
          nodes {
            number
            repository {
              nameWithOwner
            }
            status
            ...LinkedSecurityAlert
            ...LinkedSecurityAlertIcon
            ...LinkedSecurityAlertLinkItem
          }
        }
      }
    `,
    issueKey,
  )

  if (!data?.securityAlerts?.nodes || !data?.repository) return null

  const {fixedAlerts, openAlerts, dismissedAlerts, issueRepositoryName, alerts} = getLinkedSecurityAlerts(data)
  const hasMultipleStatusGroups =
    (fixedAlerts.length > 0 ? 1 : 0) + (openAlerts.length > 0 ? 1 : 0) + (dismissedAlerts.length > 0 ? 1 : 0) > 1

  // Show hovercard link if there is only one alert
  const isSingleAlert = alerts.length === 1

  // Show summary view if there is more than one alert
  const showSummaryView = alerts.length > 1

  // Stop rendering if there are no linked alerts
  if (alerts.length < 1 || !alerts[0]) return null

  const inner = isSingleAlert ? (
    <LinkedSecurityAlertLinkItem alertKey={alerts[0].key} issueRepositoryName={issueRepositoryName} isSmall={isSmall} />
  ) : showSummaryView ? (
    <div className={styles.securityAlertsContainer}>
      {isSmall ? (
        <LinkedSecurityAlertLinkItem
          alertKey={alerts[0].key}
          issueRepositoryName={issueRepositoryName}
          isSmall={isSmall}
        />
      ) : (
        <>
          <LinkedSecurityAlertIcon alertKey={alerts[0].key} />
          <LinkedAlertName alertKey={alerts[0].key} issueRepositoryName={issueRepositoryName} />
        </>
      )}
      <span>
        <span> (</span>
        <span>+{alerts.length - 1}</span>
        <span>)</span>
      </span>
    </div>
  ) : null

  if (isSmall) {
    return <div className={styles.flexRowContainer}>{inner}</div>
  }

  return (
    <>
      {showSummaryView ? (
        <ActionMenu>
          <ActionMenu.Button aria-label={LABELS.linkedSecurityAlertsLabel} className={styles.ActionMenu_Button}>
            {inner}
          </ActionMenu.Button>
          <ActionMenu.Overlay className={isSticky ? styles.overlayHidden : styles.overlayVisible} maxHeight="large">
            <ActionList>
              {/* Fixed alerts */}
              {hasMultipleStatusGroups && fixedAlerts.length > 0 && (
                <ActionList.GroupHeading aria-label={LABELS.fixedSecurityAlerts}>
                  {LABELS.fixedSecurityAlerts}
                </ActionList.GroupHeading>
              )}
              {fixedAlerts.length > 0 &&
                fixedAlerts.map(alert => (
                  <LinkedSecurityAlertListItem
                    alertKey={alert.key}
                    issueRepositoryName={issueRepositoryName}
                    key={alert.id}
                  />
                ))}

              {/* Open alerts */}
              {hasMultipleStatusGroups && openAlerts.length > 0 && (
                <>
                  {fixedAlerts.length > 0 && <ActionList.Divider />}
                  <ActionList.GroupHeading aria-label={LABELS.openSecurityAlerts}>
                    {LABELS.openSecurityAlerts}
                  </ActionList.GroupHeading>
                </>
              )}
              {openAlerts.length > 0 &&
                openAlerts.map(alert => (
                  <LinkedSecurityAlertListItem
                    alertKey={alert.key}
                    issueRepositoryName={issueRepositoryName}
                    key={alert.id}
                  />
                ))}

              {/* Dismissed alerts */}
              {hasMultipleStatusGroups && dismissedAlerts.length > 0 && (
                <>
                  {(fixedAlerts.length > 0 || openAlerts.length > 0) && <ActionList.Divider />}
                  <ActionList.GroupHeading aria-label={LABELS.dismissedSecurityAlerts}>
                    {LABELS.dismissedSecurityAlerts}
                  </ActionList.GroupHeading>
                </>
              )}
              {dismissedAlerts.length > 0 &&
                dismissedAlerts.map(alert => (
                  <LinkedSecurityAlertListItem
                    alertKey={alert.key}
                    issueRepositoryName={issueRepositoryName}
                    key={alert.id}
                  />
                ))}
            </ActionList>
          </ActionMenu.Overlay>
        </ActionMenu>
      ) : (
        <LinkedSecurityAlertsPill>{inner}</LinkedSecurityAlertsPill>
      )}
    </>
  )
}
